"use client";

import useApiSubmit from "@/hooks/useApiSubmit";
import useLocations from "@/hooks/useLocations";
import { setNameAndMessage } from "@/store/features/appSlice";
import { useAppDispatch } from "@/store/hooks";
import {
  Button,
  Card,
  CardContent,
  Field,
  Fieldset,
  Form,
  PhoneField,
  Select,
} from "@repo/ui";
import { useRouter } from "next/navigation";
import * as Yup from "yup";
import TermsCheckbox from "./TermsCheckbox";
import { useCountryCode } from "@/hooks/useCountryCode";

const FormSchema = Yup.object().shape({
  fullName: Yup.string()
    .required()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  contactNumber: Yup.string()
    .required()
    .min(10, "Too Short!")
    .max(16, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
});

type WebinarRegistrationFormProps = {
  serviceId: number;
  eventId: number;
};

const WebinarRegistrationForm = ({
  serviceId,
  eventId,
}: WebinarRegistrationFormProps) => {
  const { country } = useCountryCode();
  const { countries, cities, setSelectedCountry, loading } = useLocations();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const { handleSubmit } = useApiSubmit({
    onSuccess: (data) => {
      dispatch(setNameAndMessage(data.response));
      router.push("/thank-you-for-enquiry");
    },
  });

  const DEFAULT_FORM_VALUES = {
    fullName: "",
    contactNumber: "",
    email: "",
    countryId: "",
    cityId: "",
    enquiryType: "WEBINAR_REGISTRATION_FORM",
    terms: "false",
    serviceId: serviceId ? [serviceId] : [],
    eventId: eventId,
  };

  return (
    <Card className="md:-translate-y-40 md:absolute md:top-0 md:mt-0 mt-4">
      <CardContent className="!p-8">
        <Form
          initialValues={DEFAULT_FORM_VALUES}
          validationSchema={FormSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, setFieldValue, isSubmitting }) => (
            <>
              <Fieldset columns={1}>
                <Field
                  required
                  name="fullName"
                  label="Full Name"
                  value={values.fullName}
                  onChange={handleChange}
                />
                <PhoneField
                  label="Phone Number"
                  name="contactNumber"
                  required
                  value={values.contactNumber}
                  onChange={(phone: string) =>
                    setFieldValue("contactNumber", phone)
                  }
                  defaultCountry={country}
                />
                <Field
                  required
                  label="Email ID"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                />
                <Select
                  label="Country"
                  name="countryId"
                  required
                  value={values.countryId}
                  options={countries.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  onChange={(val) => {
                    setSelectedCountry(val);
                    setFieldValue("countryId", val);
                  }}
                />
                <Select
                  label="City"
                  name="cityId"
                  required
                  value={values.cityId}
                  disabled={cities.length === 0}
                  options={cities.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  onChange={(val) => {
                    setFieldValue("cityId", val);
                  }}
                  loading={loading}
                />
              </Fieldset>

              <TermsCheckbox
                onCheckedChange={(value) => setFieldValue("terms", value)}
                value={values.terms}
              />

              <div className="text-right mt-4">
                <Button type="submit" color="primary" loading={isSubmitting}>
                  Register now
                </Button>
              </div>
            </>
          )}
        </Form>
      </CardContent>
    </Card>
  );
};

export default WebinarRegistrationForm;
